import mermaid from "mermaid";
import {
  initMermaid,
  sanitizeMermaidCode,
} from "Components/SbMarkdownEditor/useMermaid";

/** High-DPI multiplier for crisp PDF images. */
const SCALE_FACTOR = 3;

/** Counter for unique mermaid render IDs. */
let pdfRenderCounter = 0;

/**
 * Replaces every &lt;foreignObject&gt; in the SVG with a plain SVG &lt;text&gt;
 * element at the same position. This makes the SVG canvas-safe (no tainted
 * canvas) while preserving the diagram layout that mermaid computed with
 * htmlLabels: true.
 *
 * @param {string} svgString  Raw SVG string from mermaid.render().
 * @returns {string}  SVG string without foreignObject elements.
 */
const stripForeignObjects = (svgString) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, "image/svg+xml");
  const SVG_NS = "http://www.w3.org/2000/svg";

  const foreignObjects = doc.querySelectorAll("foreignObject");

  foreignObjects.forEach((fo) => {
    // Extract all visible text from the HTML inside foreignObject.
    const text = (fo.textContent || "").trim();
    if (!text) {
      fo.parentNode?.removeChild(fo);
      return;
    }

    const x = parseFloat(fo.getAttribute("x")) || 0;
    const y = parseFloat(fo.getAttribute("y")) || 0;
    const w = parseFloat(fo.getAttribute("width")) || 100;
    const h = parseFloat(fo.getAttribute("height")) || 20;

    // Create a replacement <text> element centered in the foreignObject's box.
    const textEl = doc.createElementNS(SVG_NS, "text");
    textEl.setAttribute("x", String(x + w / 2));
    textEl.setAttribute("y", String(y + h / 2));
    textEl.setAttribute("text-anchor", "middle");
    textEl.setAttribute("dominant-baseline", "central");
    textEl.setAttribute("font-family", "system-ui, -apple-system, sans-serif");
    textEl.setAttribute("font-size", "14");
    textEl.setAttribute("fill", "#333333");

    // Split long labels into multiple <tspan> lines.
    const MAX_LINE_CHARS = 28;
    const words = text.split(/\s+/);
    const lines = [];
    let currentLine = "";

    words.forEach((word) => {
      if (currentLine.length + word.length + 1 > MAX_LINE_CHARS && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = currentLine ? `${currentLine} ${word}` : word;
      }
    });
    if (currentLine) lines.push(currentLine);

    if (lines.length === 1) {
      textEl.textContent = lines[0];
    } else {
      // Multi-line: use <tspan> elements with dy offsets.
      const lineHeight = 16;
      const startY = y + h / 2 - ((lines.length - 1) * lineHeight) / 2;

      lines.forEach((line, i) => {
        const tspan = doc.createElementNS(SVG_NS, "tspan");
        tspan.setAttribute("x", String(x + w / 2));
        tspan.setAttribute("y", String(startY + i * lineHeight));
        tspan.textContent = line;
        textEl.appendChild(tspan);
      });
    }

    fo.parentNode?.replaceChild(textEl, fo);
  });

  // Also remove any <style> blocks that reference HTML elements (div, span, etc.)
  // since those are only relevant for foreignObject content.
  // The SVG shapes/arrows use inline styles, so they're preserved.

  return new XMLSerializer().serializeToString(doc);
};

/**
 * Renders a mermaid code string to a PNG data-URL via an off-screen canvas.
 *
 * Flow:
 *   1. mermaid.render() with htmlLabels: true → beautiful SVG
 *   2. Strip <foreignObject> → replace with SVG <text> (canvas-safe)
 *   3. Load SVG into Image → draw on Canvas → PNG data-URL
 *
 * @param {string} code  Raw mermaid diagram code.
 * @returns {Promise<{dataUrl: string, width: number, height: number}|null>}
 */
const renderMermaidToPng = async (code) => {
  if (!code?.trim()) return null;

  const diagramId = `mermaid-pdf-${++pdfRenderCounter}`;

  try {
    initMermaid();
    const sanitized = sanitizeMermaidCode(code.trim());
    const { svg: svgString } = await mermaid.render(diagramId, sanitized);

    // Strip foreignObject for canvas-safe SVG.
    const safeSvg = stripForeignObjects(svgString);

    return await svgToPngDataUrl(safeSvg);
  } catch (err) {
    console.warn("[mermaidToPng] Render failed — will use text fallback:", err?.message);
    return null;
  }
};

/**
 * Converts a canvas-safe SVG string to a PNG data-URL.
 *
 * @param {string} svgString  Cleaned SVG markup (no foreignObject).
 * @returns {Promise<{dataUrl: string, width: number, height: number}>}
 */
const svgToPngDataUrl = (svgString) => {
  return new Promise((resolve, reject) => {
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgString, "image/svg+xml");
    const svgEl = svgDoc.querySelector("svg");

    if (!svgEl) {
      reject(new Error("No SVG element found in mermaid output"));
      return;
    }

    // Extract dimensions from the SVG attributes or viewBox.
    let width = parseFloat(svgEl.getAttribute("width")) || 0;
    let height = parseFloat(svgEl.getAttribute("height")) || 0;

    if (!width || !height) {
      const vb = svgEl.getAttribute("viewBox");
      if (vb) {
        const parts = vb.split(/[\s,]+/).map(Number);
        width = parts[2] || 800;
        height = parts[3] || 600;
      } else {
        width = 800;
        height = 600;
      }
    }

    // Ensure proper SVG namespace.
    svgEl.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    svgEl.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");

    const serializer = new XMLSerializer();
    const cleanSvg = serializer.serializeToString(svgEl);

    // Encode as base64 data URL (avoids CORS issues).
    const svgBase64 = btoa(unescape(encodeURIComponent(cleanSvg)));
    const dataUrl = `data:image/svg+xml;base64,${svgBase64}`;

    const img = new window.Image();

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = width * SCALE_FACTOR;
        canvas.height = height * SCALE_FACTOR;

        const ctx = canvas.getContext("2d");
        // White background.
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.scale(SCALE_FACTOR, SCALE_FACTOR);
        ctx.drawImage(img, 0, 0, width, height);

        const pngDataUrl = canvas.toDataURL("image/png");
        resolve({ dataUrl: pngDataUrl, width, height });
      } catch (canvasErr) {
        reject(canvasErr);
      }
    };

    img.onerror = (errEvent) => {
      reject(new Error(`Failed to load SVG into image: ${errEvent}`));
    };

    img.src = dataUrl;
  });
};

export default renderMermaidToPng;
